import React from 'react';
import { Button, ButtonGroup, Spinner, Table } from 'react-bootstrap';
import { useStockPeople } from '../../hooks/useStock';
import { formatCount } from '../../utils/numberFormatter';
import { formatShortDate } from '../../utils/dateFormatter';
import { TEXT, UNIT_LABELS, PERIOD_LABELS } from '../../config/stockLabels';

/**
 * «حسب الموظف» — the interim check (tasks/stock.md D12): per person, what they took from the store in
 * the period beside the turnover cleans credited to them. A warning for ops to read, never a verdict —
 * nothing here is flagged or coloured as a problem. Paired cleans count for both people, so the intro
 * tells the reader to read by pair.
 *
 * Phone: the v2.3 stacked-row pattern — each person becomes a block, each figure keeps its label.
 */
const StockPeopleTab = ({ storeId, presets, period, onPeriod }) => {
  const { data, isPending, isError, refetch, isFetching } = useStockPeople(storeId, period, true);
  const items = data?.items || [];
  const people = data?.people || [];

  return (
    <div className="stk-people">
      <p className="stk-people__intro">{TEXT.peopleIntro}</p>

      <div className="stk-people__bar">
        <ButtonGroup size="sm" aria-label={TEXT.tabPeople}>
          {(presets || []).map(key => (
            <Button key={key} variant={period === key ? 'primary' : 'outline-primary'} onClick={() => onPeriod(key)}>
              {PERIOD_LABELS[key] || key}
            </Button>
          ))}
        </ButtonGroup>
        {data?.period && (
          <span className="stk-muted">{formatShortDate(data.period.from)} – {formatShortDate(data.period.to)}</span>
        )}
        {isFetching && !isPending && <Spinner animation="border" size="sm" />}
      </div>

      {isPending && !isError && <div className="stk-state"><Spinner animation="border" size="sm" /> {TEXT.loading}</div>}
      {isError && (
        <div className="stk-error" role="alert">
          {TEXT.loadError} <Button variant="link" className="stk-link" onClick={() => refetch()}>{TEXT.refresh}</Button>
        </div>
      )}
      {data && people.length === 0 && <p className="stk-empty-line">{TEXT.peopleEmpty}</p>}

      {people.length > 0 && (
        <div className="stk-people__table">
          <Table className="align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">{TEXT.person}</th>
                <th scope="col" className="stk-num-col">{TEXT.cleans}</th>
                {items.map(item => (
                  <th scope="col" key={item.id} className="stk-num-col">
                    {item.nameAr}
                    <span className="stk-people__unit">{UNIT_LABELS[item.unit] || item.unit}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {people.map(p => (
                <tr key={p.userId}>
                  <th scope="row" className="stk-people__name">{p.name || TEXT.unnamed}</th>
                  <td className="stk-num-col" data-label={TEXT.cleans}>{formatCount(p.cleans)}</td>
                  {items.map(item => {
                    const taken = p.taken[String(item.id)] || 0;
                    return (
                      <td key={item.id} className={`stk-num-col${taken ? '' : ' is-zero'}`} data-label={item.nameAr}>
                        {formatCount(taken)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default StockPeopleTab;
